/* eslint-disable camelcase */
import React from "react";
import { Plural, Trans } from "@lingui/macro";
import { Icon } from "../../Icon";
import Rating, { RatingCount } from "../../Rating";
import Image from "../../Image";
import "./AuthorBioComponents.css";
import reverseUrl from "../../../functions/reverseUrl";
import useCurrentLanguage from "../../../functions/languages/useCurrentLanguage";
import Link from "../../Link";

export const isAuthorWeGoTrip = name => name === "WeGoTrip";

export const AuthorBioAvatar = ({ avatar, name }) => {
  const generateInitials = () => {
    const names = name.split(" ");
    const firstLetter = names[0][0].toUpperCase();
    return firstLetter || "A";
  };

  return avatar ? (
    <Image staticImage className="AuthorBioAvatar" src={avatar} loading="lazy" />
  ) : (
    <div className="AuthorBioAvatar AuthorBioAvatar--unavailable">{generateInitials()}</div>
  );
};

export const AuthorBioAbout = ({ name, products_count, place_of_stay, languages, user_id }) => {
  const variantActivities = (
    <Plural
      value={products_count}
      _0="# activities"
      one="# activity"
      few="# activities"
      many="# activities"
      other="# activities"
    />
  );
  return (
    <div className="AuthorBioAbout">
      <div className="AuthorBioAbout__title">
        {name ? <Trans>About host, {name}</Trans> : <Trans>About host, Author{user_id}</Trans>}
      </div>
      <div className="AuthorBioAbout__text">
        <Icon name="bookmark" />
        <Trans>Hosted {variantActivities}</Trans>
      </div>
      {place_of_stay && (
        <div className="AuthorBioAbout__text">
          <Icon name="house" />
          <Trans>Lives in {place_of_stay.name}</Trans>
        </div>
      )}
      {languages && languages.length > 0 && (
        <div className="AuthorBioAbout__text">
          <Icon name="globe-language" />
          <Trans>Speaks {languages.map(lang => lang.name).join(", ")}</Trans>
        </div>
      )}
    </div>
  );
};

export function AuthorBioHeader({
  name,
  avatar,
  theme,
  rating,
  ratingsCount,
  year_since,
  provider,
  isMobile,
  nickname,
}) {
  const lang = useCurrentLanguage();
  const isCard = theme === "card";

  let displayText;
  if (!isCard) {
    displayText = <Trans>Meet the host, {name}</Trans>;
  } else if (isCard && isMobile) {
    displayText = name;
  }

  const linkProps = { className: "AuthorBioHeader__href", target: "_blank" };
  const authorLink = !isCard ? reverseUrl("author", { lang, nickname }) : null;

  return (
    <div className="AuthorBioHeader">
      <Link to={authorLink} {...linkProps}>
        <AuthorBioAvatar avatar={avatar} name={name} />
      </Link>
      <div className="AuthorBioHeader__info">
        <Link to={authorLink} {...linkProps}>
          <div
            className={`AuthorBioHeader__info_title ${
              isAuthorWeGoTrip(name) && "AuthorBioHeader__info_title-wegotrip"
            }`}
          >
            {name ? displayText : <Trans>Author</Trans>}
          </div>
        </Link>

        {isCard && !isMobile && rating > 0 && ratingsCount > 0 && (
          <>
            <Rating grade={rating} starsType="small" size="large" countType="large" />
            <RatingCount
              className="AuthorBioHeader__ratingCount"
              count={ratingsCount}
              countType="large"
              size="large"
            />
          </>
        )}
        {!isAuthorWeGoTrip(name) && (
          <>
            <div className="AuthorBioHeader__info_since">
              <Trans>On WeGoTrip since {year_since}</Trans>
            </div>
            {provider && (
              <Link
                to={reverseUrl("provider", { lang, nickname: provider.nickname })}
                {...linkProps}
              >
                <div className="AuthorBioHeader__info_nickname">
                  <Trans>Published by {provider.name || provider.first_name}</Trans>
                </div>
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
